import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { InfoBanner } from '../../components/ui/info-banner';
import { 
  Calendar, Clock, CheckCircle, XCircle, RefreshCw, Loader2, 
  Plus, Eye, Trash2, Edit, CalendarDays, CalendarRange, Layers,
  User, FileText, AlertCircle, Upload, Download, X, Send, Users,
  Archive, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown, Search
} from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth header helper
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const WorkAssignments = () => {
  const { user } = useAuth();
  const [mainTab, setMainTab] = useState('daily');
  
  // Schedules state
  const [schedules, setSchedules] = useState([]);
  const [archivedSchedules, setArchivedSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [archiveSearchQuery, setArchiveSearchQuery] = useState(''); // Arşiv arama
  
  // Tab bazlı yeni sayıları (API'den alınan)
  const [newCounts, setNewCounts] = useState({ daily: 0, monthly: 0, master: 0, total: 0 });
  
  // Yeni sayıları API'den çek
  const fetchNewCounts = async () => {
    try {
      const response = await axios.get(`${API}/work/schedules/new-count`, { headers: getAuthHeaders() });
      setNewCounts(response.data || { daily: 0, monthly: 0, master: 0, total: 0 });
    } catch (error) {
      console.error('Yeni sayılar alınamadı:', error);
    }
  };
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  const [newProgressLog, setNewProgressLog] = useState({ description: '', documents: [] });
  
  // Completion modal state
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [completionData, setCompletionData] = useState({
    completion_notes: '',
    completion_date: new Date().toISOString().split('T')[0]
  });
  
  // Form state
  const [newSchedule, setNewSchedule] = useState({
    title: '',
    description: '',
    schedule_type: 'daily',
    start_date: '',
    end_date: '',
    assigned_to: [],
    priority: 'normal',
    documents: [],
    send_to_users: [],
    visible_to_users: []
  });
  
  // Document upload state
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Sorting state - varsayılan: son güncelleme (en yeni üstte)
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });

  // Arşiv açıklaması modal state'leri
  const [archiveReasonModalOpen, setArchiveReasonModalOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveTargetId, setArchiveTargetId] = useState(null);
  const [archivingItem, setArchivingItem] = useState(false);

  // Sorting functions
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-blue-600" /> 
      : <ArrowDown className="h-4 w-4 text-blue-600" />;
  };

  // Sorted schedules
  const sortedSchedules = useMemo(() => {
    if (!schedules.length) return [];
    
    return [...schedules].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'title':
          aVal = (a.title || '').toLowerCase();
          bVal = (b.title || '').toLowerCase();
          break;
        case 'owner_name':
          aVal = (a.owner_name || '').toLowerCase();
          bVal = (b.owner_name || '').toLowerCase();
          break;
        case 'created_at':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case 'start_date':
          aVal = a.start_date ? new Date(a.start_date).getTime() : 0;
          bVal = b.start_date ? new Date(b.start_date).getTime() : 0;
          break;
        case 'priority':
          const priorityOrder = { high: 3, normal: 2, low: 1 };
          aVal = priorityOrder[a.priority] || 0;
          bVal = priorityOrder[b.priority] || 0;
          break;
        case 'status':
          aVal = (a.status || '').toLowerCase();
          bVal = (b.status || '').toLowerCase();
          break;
        case 'updated_at':
          aVal = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          bVal = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          break;
        case 'completion_date':
          aVal = a.completion_date ? new Date(a.completion_date).getTime() : 0;
          bVal = b.completion_date ? new Date(b.completion_date).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [schedules, sortConfig]);

  useEffect(() => {
    if (mainTab === 'archive') {
      loadArchivedSchedules();
    } else {
      loadSchedules();
    }
    loadUsers();
    fetchNewCounts(); // Yeni sayıları çek
  }, [mainTab]);

  // Detay modal açıldığında görüldü olarak işaretle
  useEffect(() => {
    if (detailModalOpen && selectedSchedule?.id) {
      markScheduleAsSeen(selectedSchedule.id);
    }
  }, [detailModalOpen, selectedSchedule]);

  // İş programını görüldü olarak işaretle
  const markScheduleAsSeen = async (scheduleId) => {
    try {
      await axios.post(`${API}/work/schedules/${scheduleId}/mark-seen`, {}, { headers: getAuthHeaders() });
      // Yeni sayıları güncelle ve listeyi yenile
      fetchNewCounts();
      loadSchedules();
    } catch (error) {
      console.error('Plan görüldü işaretlenemedi:', error);
    }
  };

  // İş programı yeni mi kontrol et (tabloda YENİ badge için)
  const isScheduleUnread = (schedule) => {
    if (!user?.id) return false;
    // Kendi oluşturduğu programlar yeni değil
    if (schedule.owner_id === user.id) return false;
    // is_new flag backend'den geliyorsa onu kullan
    if (schedule.is_new !== undefined) return schedule.is_new;
    // Aksi halde read_by kontrolü yap
    return !schedule.read_by?.includes(user.id);
  };

  const loadSchedules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work/schedules`, {
        params: { schedule_type: mainTab },
        headers: getAuthHeaders()
      });
      setSchedules(res.data || []);
    } catch (error) {
      console.error('İş planları yüklenirken hata:', error);
      toast.error('İş planları yüklenemedi');
    }
    setLoading(false);
  };

  const loadArchivedSchedules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work/schedules/archived`, {
        headers: getAuthHeaders()
      });
      setArchivedSchedules(res.data || []);
    } catch (error) {
      console.error('Arşivlenmiş iş planları yüklenirken hata:', error);
      toast.error('Arşivlenmiş iş planları yüklenemedi');
    }
    setLoading(false);
  };

  const restoreSchedule = async (scheduleId) => {
    try {
      await axios.post(`${API}/work/schedules/${scheduleId}/restore`, {}, { headers: getAuthHeaders() });
      toast.success('İş planı arşivden geri yüklendi');
      loadArchivedSchedules();
    } catch (error) {
      console.error('Geri yükleme hatası:', error);
      toast.error('İş planı geri yüklenemedi');
    }
  };

  // İş planını arşive taşı
  const openArchiveReasonModal = (scheduleId) => {
    setArchiveTargetId(scheduleId);
    setArchiveReason('');
    setArchiveReasonModalOpen(true);
  };

  const handleConfirmArchiveSchedule = async () => {
    if (!archiveReason.trim()) {
      toast.error('Arşiv açıklaması zorunludur');
      return;
    }
    
    setArchivingItem(true);
    try {
      await axios.post(`${API}/work/schedules/${archiveTargetId}/archive`, { archive_reason: archiveReason }, { headers: getAuthHeaders() });
      toast.success('İş planı arşive taşındı');
      loadSchedules();
      window.dispatchEvent(new Event('unread-counts-updated'));
      setArchiveReasonModalOpen(false);
      setArchiveReason('');
      setArchiveTargetId(null);
    } catch (error) {
      console.error('Arşivleme hatası:', error);
      toast.error('İş planı arşivlenemedi');
    } finally {
      setArchivingItem(false);
    }
  };

  const handleArchiveSchedule = async (scheduleId) => {
    openArchiveReasonModal(scheduleId);
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/calendar/users-for-share`, { headers: getAuthHeaders() });
      setUsers(res.data.users || []);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
    }
  };

  // Document upload handler
  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewSchedule(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeDocument = (index) => {
    setNewSchedule(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const toggleUserSelection = (userId) => {
    setNewSchedule(prev => {
      const users = prev.send_to_users.includes(userId)
        ? prev.send_to_users.filter(id => id !== userId)
        : [...prev.send_to_users, userId];
      return { ...prev, send_to_users: users };
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isPreviewable = (type) => {
    return type && (type.startsWith('image/') || type === 'application/pdf');
  };

  const handleCreateSchedule = async () => {
    if (!newSchedule.title) {
      toast.error('İş planı başlığı gerekli');
      return;
    }
    
    try {
      // send_to_users seçilen kişiler hem gönderilecek hem görebilecek kişiler olsun
      const scheduleData = {
        ...newSchedule,
        schedule_type: mainTab,
        owner_name: user?.name || 'Bilinmiyor',
        visible_to_users: [...new Set([...(newSchedule.visible_to_users || []), ...(newSchedule.send_to_users || [])])]
      };
      console.log('Creating schedule with data:', scheduleData);
      console.log('visible_to_users:', scheduleData.visible_to_users);
      await axios.post(`${API}/work/schedules`, scheduleData, { headers: getAuthHeaders() });
      toast.success('İş planı oluşturuldu');
      setCreateModalOpen(false);
      setNewSchedule({
        title: '',
        description: '',
        schedule_type: mainTab,
        start_date: '',
        end_date: '',
        assigned_to: [],
        priority: 'normal',
        documents: [],
        send_to_users: [],
        visible_to_users: []
      });
      loadSchedules();
      fetchNewCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İş planı oluşturulamadı');
    }
  };

  const handleUpdateSchedule = async () => {
    if (!selectedSchedule) return;
    
    try {
      await axios.put(`${API}/work/schedules/${selectedSchedule.id}`, {
        title: selectedSchedule.title,
        description: selectedSchedule.description,
        start_date: selectedSchedule.start_date,
        end_date: selectedSchedule.end_date,
        priority: selectedSchedule.priority,
        status: selectedSchedule.status,
        visible_to_users: selectedSchedule.visible_to_users || []
      }, { headers: getAuthHeaders() });
      toast.success('İş planı güncellendi');
      setEditModalOpen(false);
      loadSchedules();
      fetchNewCounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İş planı güncellenemedi');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Bu iş planını silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/work/schedules/${scheduleId}`, { headers: getAuthHeaders() });
      toast.success('İş planı silindi');
      loadSchedules();
      setDetailModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İş planı silinemedi');
    }
  };

  const handleAcceptSchedule = async (scheduleId) => {
    try {
      await axios.post(`${API}/work/schedules/${scheduleId}/accept`, {}, { headers: getAuthHeaders() });
      toast.success('İş planı kabul edildi');
      loadSchedules();
    } catch (error) {
      toast.error('İş planı kabul edilemedi');
    }
  };

  const loadProgressLogs = async (scheduleId) => {
    try {
      const res = await axios.get(`${API}/work/schedules/${scheduleId}/progress`, { headers: getAuthHeaders() });
      setProgressLogs(res.data.progress_logs || []);
    } catch (error) {
      console.error('Güncel durum yüklenemedi:', error);
    }
  };

  const handleOpenProgressModal = (schedule) => {
    setSelectedSchedule(schedule);
    loadProgressLogs(schedule.id);
    setProgressModalOpen(true);
  };

  const handleProgressDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewProgressLog(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleAddProgressLog = async () => {
    if (!newProgressLog.description.trim()) {
      toast.error('Açıklama gerekli');
      return;
    }
    try {
      await axios.post(`${API}/work/schedules/${selectedSchedule.id}/progress`, newProgressLog, { headers: getAuthHeaders() });
      toast.success('Güncel durum eklendi');
      setNewProgressLog({ description: '', documents: [] });
      loadProgressLogs(selectedSchedule.id);
      loadSchedules();
    } catch (error) {
      toast.error('Güncel durum eklenemedi');
    }
  };

  const handleStatusChange = async (scheduleId, newStatus) => {
    try {
      await axios.put(`${API}/work/schedules/${scheduleId}`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success('Durum güncellendi');
      loadSchedules();
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const handleOpenCompletionModal = (schedule) => {
    setSelectedSchedule(schedule);
    setCompletionData({
      completion_notes: '',
      completion_date: new Date().toISOString().split('T')[0]
    });
    setDetailModalOpen(false);
    setCompletionModalOpen(true);
  };

  const handleCompleteWithDetails = async () => {
    if (!selectedSchedule) return;
    
    if (!completionData.completion_notes.trim()) {
      toast.error('Tamamlama açıklaması gerekli');
      return;
    }
    
    try {
      // Tamamlanma tarihi ve saati otomatik olarak şimdiki zaman
      const completionDateTime = new Date().toISOString();
      
      await axios.put(`${API}/work/schedules/${selectedSchedule.id}`, {
        status: 'completed',
        completion_notes: completionData.completion_notes,
        completion_date: completionDateTime,
        completed_by: user?.id,
        completed_by_name: user?.name
      }, { headers: getAuthHeaders() });
      toast.success('İş planı tamamlandı');
      setCompletionModalOpen(false);
      loadSchedules();
    } catch (error) {
      toast.error('İş planı tamamlanamadı');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      planned: { label: 'Planlandı', color: 'bg-blue-100 text-blue-700', icon: Clock },
      in_progress: { label: 'Devam Ediyor', color: 'bg-amber-100 text-amber-700', icon: RefreshCw },
      completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      cancelled: { label: 'İptal Edildi', color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const c = config[status] || config.planned;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} gap-1`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { label: 'Düşük', color: 'bg-gray-100 text-gray-600' },
      normal: { label: 'Normal', color: 'bg-blue-100 text-blue-600' },
      high: { label: 'Yüksek', color: 'bg-orange-100 text-orange-600' },
      urgent: { label: 'Acil', color: 'bg-red-100 text-red-600' }
    };
    const c = config[priority] || config.normal;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getTabIcon = (type) => {
    switch(type) {
      case 'daily': return CalendarDays;
      case 'monthly': return CalendarRange;
      case 'master': return Layers;
      case 'archive': return Archive;
      default: return Calendar;
    }
  };

  const getTabLabel = (type) => {
    switch(type) {
      case 'daily': return 'GÜNLÜK İŞ PLANI';
      case 'monthly': return 'AYLIK İŞ PLANI';
      case 'master': return 'MASTER İŞ PLANI';
      case 'archive': return 'ARŞİV';
      default: return type;
    }
  };

  const renderScheduleTable = () => {
    if (schedules.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">İş Planı Bulunmuyor</h3>
            <p className="text-gray-500">Bu kategoride iş planı yok</p>
            <Button 
              onClick={() => setCreateModalOpen(true)}
              className="mt-4 gap-2 bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
            >
              <Plus className="h-4 w-4" />
              İş Planı Ekle
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center gap-2">
                    İş Planı {getSortIcon('title')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('owner_name')}
                >
                  <div className="flex items-center gap-2">
                    Oluşturan {getSortIcon('owner_name')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-2">
                    Oluşturulma {getSortIcon('created_at')}
                  </div>
                </TableHead>
                <TableHead>Kabul Edenler</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('start_date')}
                >
                  <div className="flex items-center gap-2">
                    Tarih Aralığı {getSortIcon('start_date')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('priority')}
                >
                  <div className="flex items-center gap-2">
                    Öncelik {getSortIcon('priority')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-2">
                    Durum {getSortIcon('status')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('updated_at')}
                >
                  <div className="flex items-center gap-2">
                    Son Güncelleme {getSortIcon('updated_at')}
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('completion_date')}
                >
                  <div className="flex items-center gap-2">
                    Tamamlanma Zamanı {getSortIcon('completion_date')}
                  </div>
                </TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSchedules.map(schedule => {
                const isUnread = isScheduleUnread(schedule);
                return (
                <TableRow key={schedule.id} data-testid={`schedule-row-${schedule.id}`} className={isUnread ? 'bg-orange-50' : ''}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div>
                        <p className={`font-medium ${isUnread ? 'text-blue-900' : ''}`}>{schedule.title}</p>
                        {schedule.description && (
                          <p className="text-sm text-gray-500 truncate max-w-[200px]">{schedule.description}</p>
                        )}
                      </div>
                      {isUnread && (
                        <Badge className="bg-orange-500 text-white text-xs animate-pulse">YENİ</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      {schedule.owner?.name || schedule.owner_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-600">
                      {schedule.created_at 
                        ? new Date(schedule.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {schedule.accepted_by && schedule.accepted_by.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {schedule.accepted_by.map((a, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-green-50 text-green-700">
                            {a.user_name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {schedule.start_date ? (
                      <div className="text-sm">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          {new Date(schedule.start_date).toLocaleDateString('tr-TR')}
                        </div>
                        {schedule.end_date && (
                          <div className="text-gray-500">
                            → {new Date(schedule.end_date).toLocaleDateString('tr-TR')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>{getPriorityBadge(schedule.priority)}</TableCell>
                  <TableCell>{getStatusBadge(schedule.status)}</TableCell>
                  <TableCell>
                    {schedule.updated_at ? (
                      <div className="text-sm text-blue-600 font-medium">
                        {new Date(schedule.updated_at).toLocaleDateString('tr-TR', { 
                          day: '2-digit', month: '2-digit', year: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {schedule.status === 'completed' && schedule.completion_date ? (
                      <div className="text-sm text-green-600">
                        {new Date(schedule.completion_date).toLocaleDateString('tr-TR', { 
                          day: '2-digit', month: '2-digit', year: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => { 
                          setSelectedSchedule(schedule); 
                          setDetailModalOpen(true); 
                          if (isUnread) {
                            markScheduleAsSeen(schedule.id);
                          }
                        }}
                        data-testid={`view-schedule-${schedule.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {schedule.owner_id === user?.id && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => { setSelectedSchedule({...schedule}); setEditModalOpen(true); }}
                            data-testid={`edit-schedule-${schedule.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            title="Arşive Taşı"
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            onClick={() => handleArchiveSchedule(schedule.id)}
                            data-testid={`archive-schedule-${schedule.id}`}
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="work-schedule">
      {/* Bilgilendirme Notu */}
      <InfoBanner id="work-assignments-info" variant="info">
        <strong>İş Programı</strong> bölümünde <strong>GÜNLÜK İŞ PLANI</strong>, <strong>AYLIK İŞ PLANI</strong>, <strong>MASTER İŞ PLANLARINIZI</strong> ekleyebilirsiniz. 
        İstediğiniz kişileri İş Programına İş Planı eklerken dahil edebilirsiniz. 
        İş Programlarına güncel durum ekleyerek veya güncel durumlarını kontrol ederek son durumlarını bildirebilir ve öğrenebilirsiniz.
      </InfoBanner>

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">İŞ PROGRAMI</h1>
          <p className="text-gray-500">Günlük, aylık ve master iş planlarınızı yönetin</p>
        </div>
        <Button onClick={loadSchedules} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Yenile
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--brand-600))]" />
        </div>
      ) : (
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-4">
            {['daily', 'monthly', 'master', 'archive'].map(type => {
              const Icon = getTabIcon(type);
              const unreadCount = type === 'daily' ? newCounts.daily 
                : type === 'monthly' ? newCounts.monthly 
                : type === 'master' ? newCounts.master 
                : 0;
              return (
                <TabsTrigger 
                  key={type} 
                  value={type} 
                  className="gap-2"
                  data-testid={`tab-${type}`}
                >
                  <Icon className="h-4 w-4" />
                  {getTabLabel(type)}
                  {unreadCount > 0 && (
                    <Badge className="ml-1 bg-orange-500 text-white animate-pulse">{unreadCount} yeni</Badge>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* GÜNLÜK İŞ PLANI */}
          <TabsContent value="daily" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button 
                onClick={() => setCreateModalOpen(true)}
                className="gap-2 bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
                data-testid="add-daily-schedule-btn"
              >
                <Plus className="h-4 w-4" />
                İŞ PLANI EKLE
              </Button>
            </div>
            {renderScheduleTable()}
          </TabsContent>

          {/* AYLIK İŞ PLANI */}
          <TabsContent value="monthly" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button 
                onClick={() => setCreateModalOpen(true)}
                className="gap-2 bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
                data-testid="add-monthly-schedule-btn"
              >
                <Plus className="h-4 w-4" />
                İŞ PLANI EKLE
              </Button>
            </div>
            {renderScheduleTable()}
          </TabsContent>

          {/* MASTER İŞ PLANI */}
          <TabsContent value="master" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button 
                onClick={() => setCreateModalOpen(true)}
                className="gap-2 bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
                data-testid="add-master-schedule-btn"
              >
                <Plus className="h-4 w-4" />
                İŞ PLANI EKLE
              </Button>
            </div>
            {renderScheduleTable()}
          </TabsContent>

          {/* ARŞİV */}
          <TabsContent value="archive" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Archive className="h-5 w-5 text-gray-500" />
                    Arşivlenmiş İş Planları
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Arşivde ara..."
                        value={archiveSearchQuery}
                        onChange={(e) => setArchiveSearchQuery(e.target.value)}
                        className="pl-10 w-64"
                        data-testid="assignments-archive-search"
                      />
                    </div>
                    <Badge variant="outline">
                      {archivedSchedules.filter(s => 
                        !archiveSearchQuery || 
                        s.title?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                        s.archived_by_name?.toLowerCase().includes(archiveSearchQuery.toLowerCase())
                      ).length} plan
                    </Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {archivedSchedules.filter(s => 
                  !archiveSearchQuery || 
                  s.title?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                  s.archived_by_name?.toLowerCase().includes(archiveSearchQuery.toLowerCase())
                ).length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Archive className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Arşivde iş planı bulunmuyor</p>
                    <p className="text-sm">Silinen iş planları burada görüntülenecek</p>
                  </div>
                ) : (
                  <div className="max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Başlık</TableHead>
                        <TableHead>Tür</TableHead>
                        <TableHead>Arşivlenme Tarihi</TableHead>
                        <TableHead>Arşivleyen</TableHead>
                        <TableHead>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archivedSchedules.filter(s => 
                        !archiveSearchQuery || 
                        s.title?.toLowerCase().includes(archiveSearchQuery.toLowerCase()) ||
                        s.archived_by_name?.toLowerCase().includes(archiveSearchQuery.toLowerCase())
                      ).map(schedule => (
                        <TableRow key={schedule.id}>
                          <TableCell className="font-medium">{schedule.title}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {schedule.schedule_type === 'daily' ? 'Günlük' : schedule.schedule_type === 'monthly' ? 'Aylık' : schedule.schedule_type === 'master' ? 'Master' : schedule.schedule_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {schedule.archived_at ? new Date(schedule.archived_at).toLocaleDateString('tr-TR') : '-'}
                          </TableCell>
                          <TableCell>{schedule.archived_by_name || '-'}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => {
                                  setSelectedSchedule(schedule);
                                  setDetailModalOpen(true);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => restoreSchedule(schedule.id)}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* İş Planı Oluşturma Modalı */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              Yeni İş Planı Ekle - {getTabLabel(mainTab)}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="schedule-title">İş Planı Başlığı *</Label>
              <Input 
                id="schedule-title"
                value={newSchedule.title}
                onChange={(e) => setNewSchedule({ ...newSchedule, title: e.target.value })}
                placeholder="İş planı başlığını girin"
                data-testid="schedule-title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="schedule-desc">Açıklama</Label>
              <Textarea 
                id="schedule-desc"
                value={newSchedule.description}
                onChange={(e) => setNewSchedule({ ...newSchedule, description: e.target.value })}
                placeholder="İş planı açıklaması"
                rows={3}
                data-testid="schedule-desc-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="schedule-start">Başlangıç Tarihi</Label>
                <Input 
                  id="schedule-start"
                  type="date"
                  value={newSchedule.start_date}
                  onChange={(e) => setNewSchedule({ ...newSchedule, start_date: e.target.value })}
                  data-testid="schedule-start-input"
                />
              </div>
              
              <div>
                <Label htmlFor="schedule-end">Bitiş Tarihi</Label>
                <Input 
                  id="schedule-end"
                  type="date"
                  value={newSchedule.end_date}
                  onChange={(e) => setNewSchedule({ ...newSchedule, end_date: e.target.value })}
                  data-testid="schedule-end-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="schedule-priority">Öncelik</Label>
              <Select 
                value={newSchedule.priority} 
                onValueChange={(val) => setNewSchedule({ ...newSchedule, priority: val })}
              >
                <SelectTrigger data-testid="schedule-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Düşük</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Yüksek</SelectItem>
                  <SelectItem value="urgent">Acil</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Belge Yükleme */}
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Upload className="h-4 w-4 text-blue-500" />
                  Belgeler
                </Label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleDocumentUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  />
                  <span className="text-sm text-blue-600 hover:underline">+ Belge Ekle</span>
                </label>
              </div>
              
              {newSchedule.documents.length > 0 && (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {newSchedule.documents.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{doc.name}</span>
                        <span className="text-xs text-gray-400">({formatFileSize(doc.size)})</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                        onClick={() => removeDocument(idx)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Kişilere Gönder */}
            <div className="border rounded-lg p-3 space-y-3">
              <Label className="flex items-center gap-2">
                <Send className="h-4 w-4 text-green-500" />
                Kişilere Gönder (Opsiyonel)
              </Label>
              <ScrollArea className="h-32">
                <div className="space-y-1">
                  {users.map(u => (
                    <div
                      key={u.id}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                        newSchedule.send_to_users.includes(u.id) ? 'bg-green-100' : 'hover:bg-gray-100'
                      }`}
                      onClick={() => toggleUserSelection(u.id)}
                    >
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{u.name}</span>
                      </div>
                      {newSchedule.send_to_users.includes(u.id) && (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                  ))}
                  {users.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">Paylaşılacak kullanıcı yok</p>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreateSchedule}
              className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
              data-testid="submit-schedule-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              İş Planı Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş Planı Detay Modalı */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              İş Planı Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedSchedule && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-[hsl(var(--brand-100))] text-[hsl(var(--brand-700))]">
                    {getTabLabel(selectedSchedule.schedule_type)}
                  </Badge>
                  {getStatusBadge(selectedSchedule.status)}
                </div>
                <h3 className="font-bold text-lg mb-2">{selectedSchedule.title}</h3>
                {selectedSchedule.description && (
                  <p className="text-gray-600">{selectedSchedule.description}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Oluşturan</Label>
                  <p className="font-medium">{selectedSchedule.owner?.name || selectedSchedule.owner_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturulma Tarihi</Label>
                  <p className="font-medium">
                    {selectedSchedule.created_at 
                      ? new Date(selectedSchedule.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Öncelik</Label>
                  <div className="mt-1">{getPriorityBadge(selectedSchedule.priority)}</div>
                </div>
                {selectedSchedule.start_date && (
                  <div>
                    <Label className="text-gray-500">Başlangıç</Label>
                    <p className="font-medium">{new Date(selectedSchedule.start_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                )}
                {selectedSchedule.end_date && (
                  <div>
                    <Label className="text-gray-500">Bitiş</Label>
                    <p className="font-medium">{new Date(selectedSchedule.end_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                )}
              </div>

              {/* Belgeler */}
              {selectedSchedule.documents && selectedSchedule.documents.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Ekli Belgeler</Label>
                  <div className="space-y-2">
                    {selectedSchedule.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <div>
                            <p className="font-medium text-sm">{doc.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {isPreviewable(doc.type) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewDoc(doc)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = doc.data;
                              link.download = doc.name;
                              link.click();
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gönderilen Kişiler */}
              {selectedSchedule.send_to_users && selectedSchedule.send_to_users.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Gönderilen Kişiler</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedSchedule.send_to_users.map((userId, idx) => {
                      const u = users.find(us => us.id === userId);
                      return (
                        <Badge key={idx} variant="outline" className="gap-1">
                          <User className="h-3 w-3" />
                          {u?.name || userId}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Kabul Edenler */}
              {selectedSchedule.accepted_by && selectedSchedule.accepted_by.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Kabul Edenler</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedSchedule.accepted_by.map((a, idx) => (
                      <Badge key={idx} className="bg-green-100 text-green-700">
                        {a.user_name} - {new Date(a.accepted_at).toLocaleDateString('tr-TR')}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Tamamlanmış/İptal edilmiş için sadece Güncel Durum */}
              {(selectedSchedule.status === 'completed' || selectedSchedule.status === 'cancelled') && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Geçmiş Kayıtlar</Label>
                  <Button 
                    onClick={() => {
                      setDetailModalOpen(false);
                      handleOpenProgressModal(selectedSchedule);
                    }}
                    variant="outline"
                    className="border-blue-500 text-blue-600 hover:bg-blue-50"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Güncel Durum Geçmişi
                  </Button>
                </div>
              )}

              {/* Durum Değiştirme - Aktif planlar için */}
              {selectedSchedule.status !== 'completed' && selectedSchedule.status !== 'cancelled' && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">İşlemler</Label>
                  <div className="flex gap-2 flex-wrap">
                    {/* Başlat - Herkes basabilir */}
                    {selectedSchedule.status === 'planned' && (
                      <Button 
                        onClick={() => {
                          handleAcceptSchedule(selectedSchedule.id);
                          setDetailModalOpen(false);
                        }}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Başlat
                      </Button>
                    )}
                    
                    {/* Güncel Durum - Herkes görebilir */}
                    <Button 
                      onClick={() => {
                        setDetailModalOpen(false);
                        handleOpenProgressModal(selectedSchedule);
                      }}
                      variant="outline"
                      className="border-blue-500 text-blue-600 hover:bg-blue-50"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Güncel Durum
                    </Button>
                    
                    {/* Tamamla ve İptal - Sadece oluşturan kişi */}
                    {selectedSchedule.owner_id === user?.id && selectedSchedule.status === 'in_progress' && (
                      <>
                        <Button 
                          onClick={() => handleOpenCompletionModal(selectedSchedule)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Tamamla
                        </Button>
                        <Button 
                          onClick={() => handleStatusChange(selectedSchedule.id, 'cancelled')}
                          variant="destructive"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          İptal Et
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* İş Planı Düzenleme Modalı */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              İş Planı Düzenle
            </DialogTitle>
          </DialogHeader>
          
          {selectedSchedule && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-title">İş Planı Başlığı</Label>
                <Input 
                  id="edit-title"
                  value={selectedSchedule.title}
                  onChange={(e) => setSelectedSchedule({ ...selectedSchedule, title: e.target.value })}
                  data-testid="edit-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-desc">Açıklama</Label>
                <Textarea 
                  id="edit-desc"
                  value={selectedSchedule.description || ''}
                  onChange={(e) => setSelectedSchedule({ ...selectedSchedule, description: e.target.value })}
                  rows={3}
                  data-testid="edit-desc-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-start">Başlangıç Tarihi</Label>
                  <Input 
                    id="edit-start"
                    type="date"
                    value={selectedSchedule.start_date || ''}
                    onChange={(e) => setSelectedSchedule({ ...selectedSchedule, start_date: e.target.value })}
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-end">Bitiş Tarihi</Label>
                  <Input 
                    id="edit-end"
                    type="date"
                    value={selectedSchedule.end_date || ''}
                    onChange={(e) => setSelectedSchedule({ ...selectedSchedule, end_date: e.target.value })}
                  />
                </div>
              </div>
              
              <div>
                <Label>Öncelik</Label>
                <Select 
                  value={selectedSchedule.priority} 
                  onValueChange={(val) => setSelectedSchedule({ ...selectedSchedule, priority: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Düşük</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">Yüksek</SelectItem>
                    <SelectItem value="urgent">Acil</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Görüntüleyebilecek Kişiler */}
              <div className="border rounded-lg p-3 space-y-3">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-500" />
                  Görüntüleyebilecek Kişiler
                </Label>
                <p className="text-xs text-gray-500">Bu planı kimlerin görebileceğini seçin.</p>
                <ScrollArea className="h-32">
                  <div className="space-y-1">
                    {users.map(u => (
                      <div
                        key={u.id}
                        className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                          (selectedSchedule.visible_to_users || []).includes(u.id) ? 'bg-blue-100' : 'hover:bg-gray-100'
                        }`}
                        onClick={() => {
                          setSelectedSchedule(prev => {
                            const current = prev.visible_to_users || [];
                            return {
                              ...prev,
                              visible_to_users: current.includes(u.id)
                                ? current.filter(id => id !== u.id)
                                : [...current, u.id]
                            };
                          });
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{u.name}</span>
                        </div>
                        {(selectedSchedule.visible_to_users || []).includes(u.id) && (
                          <CheckCircle className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                {(selectedSchedule.visible_to_users || []).length > 0 && (
                  <p className="text-xs text-blue-600">{(selectedSchedule.visible_to_users || []).length} kişi seçildi</p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleUpdateSchedule}
              className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
              data-testid="save-schedule-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Belge Önizleme Modal */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-500" />
              {previewDoc?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg min-h-[400px] max-h-[70vh] overflow-auto">
            {previewDoc?.type?.startsWith('image/') ? (
              <img 
                src={previewDoc?.data} 
                alt={previewDoc?.name}
                className="max-w-full max-h-[65vh] object-contain rounded shadow-lg"
              />
            ) : previewDoc?.type === 'application/pdf' ? (
              <iframe
                src={previewDoc?.data}
                title={previewDoc?.name}
                className="w-full h-[65vh] border-0 rounded"
              />
            ) : (
              <p className="text-gray-500">Bu dosya türü önizlenemiyor</p>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                const link = document.createElement('a');
                link.href = previewDoc?.data;
                link.download = previewDoc?.name;
                link.click();
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              İndir
            </Button>
            <Button onClick={() => setPreviewDoc(null)}>
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Güncel Durum Modal */}
      <Dialog open={progressModalOpen} onOpenChange={setProgressModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              Güncel Durum - {selectedSchedule?.title}
            </DialogTitle>
          </DialogHeader>
          
          {/* Yeni Güncelleme Ekle - Sadece aktif planlar için */}
          {selectedSchedule?.status !== 'completed' && selectedSchedule?.status !== 'cancelled' && (
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Plus className="h-5 w-5 text-green-600" />
                <Label className="font-semibold">Yeni Güncelleme Ekle</Label>
              </div>
              <Textarea
                value={newProgressLog.description}
                onChange={(e) => setNewProgressLog(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ne yapıldı? Açıklama yazın..."
                rows={3}
              />
              <div className="flex items-center justify-between">
                <label className="cursor-pointer flex items-center gap-2 text-sm text-blue-600 hover:underline">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleProgressDocUpload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  />
                  <Upload className="h-4 w-4" />
                  Belge/Fotoğraf Ekle
                </label>
                <Button onClick={handleAddProgressLog} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Ekle
                </Button>
              </div>
              {newProgressLog.documents.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {newProgressLog.documents.map((doc, idx) => (
                    <Badge key={idx} variant="outline" className="gap-1">
                      <FileText className="h-3 w-3" />
                      {doc.name}
                      <button
                        onClick={() => setNewProgressLog(prev => ({
                          ...prev,
                          documents: prev.documents.filter((_, i) => i !== idx)
                        }))}
                        className="ml-1 text-red-500 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Tamamlanmış/İptal edilmiş için bilgi mesajı */}
          {(selectedSchedule?.status === 'completed' || selectedSchedule?.status === 'cancelled') && (
            <div className="border rounded-lg p-4 bg-blue-50 text-blue-700">
              <p className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Bu iş planı {selectedSchedule?.status === 'completed' ? 'tamamlanmış' : 'iptal edilmiş'}. Sadece geçmiş kayıtları görüntüleyebilirsiniz.
              </p>
            </div>
          )}

          {/* Güncellemeler Listesi */}
          <div className="space-y-4 mt-4">
            <Label className="font-semibold">Güncelleme Geçmişi</Label>
            {progressLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Henüz güncelleme yapılmamış</p>
              </div>
            ) : (
              <div className="space-y-3">
                {progressLogs.map((log) => (
                  <div key={log.id} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{log.user_name}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(log.created_at).toLocaleDateString('tr-TR', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-2">{log.description}</p>
                    {log.documents && log.documents.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
                        {log.documents.map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1">
                            <FileText className="h-3 w-3 text-gray-500" />
                            <span className="text-xs">{doc.name}</span>
                            {isPreviewable(doc.type) && (
                              <button
                                onClick={() => setPreviewDoc(doc)}
                                className="text-blue-500 hover:text-blue-700 ml-1"
                              >
                                <Eye className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = doc.data;
                                link.download = doc.name;
                                link.click();
                              }}
                              className="text-green-500 hover:text-green-700 ml-1"
                            >
                              <Download className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tamamlama Modal */}
      <Dialog open={completionModalOpen} onOpenChange={setCompletionModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              İş Planını Tamamla
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="font-medium">{selectedSchedule?.title}</p>
              <p className="text-sm text-gray-500">{selectedSchedule?.description}</p>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg text-blue-700 text-sm">
              <p className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Tamamlanma tarihi ve saati otomatik olarak kaydedilecektir.
              </p>
            </div>
            
            <div>
              <Label htmlFor="completion-notes">Tamamlama Açıklaması *</Label>
              <Textarea 
                id="completion-notes"
                value={completionData.completion_notes}
                onChange={(e) => setCompletionData(prev => ({ ...prev, completion_notes: e.target.value }))}
                placeholder="İş planının nasıl tamamlandığını açıklayın..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompletionModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCompleteWithDetails}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Tamamla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Arşiv Açıklaması Modal */}
      <Dialog open={archiveReasonModalOpen} onOpenChange={setArchiveReasonModalOpen}>
        <DialogContent className="max-w-md" data-testid="archive-reason-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Archive className="h-5 w-5" />
              İş Planını Arşive Gönder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Bu iş planını arşive göndermek için bir açıklama yazmanız gerekmektedir.
            </p>
            <div className="space-y-2">
              <Label htmlFor="archive-reason" className="text-sm font-medium">
                Arşiv Açıklaması <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="archive-reason"
                placeholder="Arşivleme nedenini yazın..."
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="archive-reason-input"
              />
              <p className="text-xs text-gray-500">
                Bu açıklama arşivlenen öğenin detaylarında görünecektir.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setArchiveReasonModalOpen(false);
                setArchiveReason('');
                setArchiveTargetId(null);
              }}
              disabled={archivingItem}
            >
              İptal
            </Button>
            <Button 
              onClick={handleConfirmArchiveSchedule}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={archivingItem || !archiveReason.trim()}
              data-testid="confirm-archive-btn"
            >
              {archivingItem ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Archive className="h-4 w-4 mr-2" />
              )}
              Arşive Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkAssignments;
